// Tab Phân tích đa chiều: so sánh theo loại / danh mục / đơn vị ban hành / khối / người tạo trên cùng bộ chỉ số,
// sắp xếp được, bấm từng dòng để mở danh sách đã lọc.
import { Segmented, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { ChevronRight } from 'lucide-react';
import { fmtSize } from '../logic';
import { SLA_DAYS } from '../stats';
import { DIMENSION_LABEL, type DimRow, type Dimension } from '../analytics';
import { COLORS, Card, Legend, RateBar, fmtDays, pct, rateColor, type DashCtx } from './ui';

export default function DimensionsTab({ ctx, dim, setDim, rows }: { ctx: DashCtx; dim: Dimension; setDim: (d: Dimension) => void; rows: DimRow[] }) {
  const chart = rows.slice(0, 12).map(r => ({ name: r.label, active: r.active, other: r.total - r.active, read: Math.round(r.readRate * 100) }));
  const sum = (k: keyof DimRow) => rows.reduce((s, r) => s + (r[k] as number), 0);

  const columns: ColumnsType<DimRow> = [
    {
      title: DIMENSION_LABEL[dim], dataIndex: 'label', fixed: 'left', width: 200,
      render: (v, r) => <span className="flex items-center gap-1 font-medium text-slate-700">{v}{r.filter && <ChevronRight size={14} className="text-slate-300" />}</span>,
    },
    { title: 'Tổng', dataIndex: 'total', width: 80, align: 'right', sorter: (a, b) => a.total - b.total, defaultSortOrder: 'descend' },
    { title: 'Hiệu lực', dataIndex: 'active', width: 90, align: 'right', sorter: (a, b) => a.active - b.active },
    { title: <Tooltip title="Ban hành trong kỳ đang chọn">Ban hành kỳ này</Tooltip>, dataIndex: 'issued', width: 120, align: 'right', sorter: (a, b) => a.issued - b.issued },
    { title: 'Chờ duyệt', dataIndex: 'pending', width: 100, align: 'right', sorter: (a, b) => a.pending - b.pending, render: v => (v ? <span className="font-medium text-amber-600">{v}</span> : <span className="text-slate-300">0</span>) },
    {
      title: 'Tỷ lệ đọc', dataIndex: 'readRate', width: 150, sorter: (a, b) => a.readRate - b.readRate,
      render: (v, r) => (r.readPairs ? <div className="flex items-center gap-2"><span className="flex-1"><RateBar value={v} color={rateColor(v)} height={6} /></span><span className="w-9 text-right text-xs">{pct(v)}</span></div> : <span className="text-slate-300">—</span>),
    },
    {
      title: <Tooltip title="Thời gian duyệt trung bình các lượt ban hành trong kỳ">TG duyệt</Tooltip>, dataIndex: 'avgApproval', width: 100, align: 'right', sorter: (a, b) => a.avgApproval - b.avgApproval,
      render: (v, r) => (r.decisions ? <span className={v > SLA_DAYS ? 'font-medium text-red-500' : ''}>{fmtDays(v)}</span> : <span className="text-slate-300">—</span>),
    },
    {
      title: <Tooltip title="Tỷ lệ bị từ chối trên số quyết định duyệt trong kỳ">Từ chối</Tooltip>, dataIndex: 'rejectRate', width: 90, align: 'right', sorter: (a, b) => a.rejectRate - b.rejectRate,
      render: (v, r) => (r.decisions ? <span className={v >= 0.3 ? 'font-medium text-red-500' : ''}>{pct(v)}</span> : <span className="text-slate-300">—</span>),
    },
    { title: 'Sắp hết hạn', dataIndex: 'expiring', width: 100, align: 'right', sorter: (a, b) => a.expiring - b.expiring, render: v => (v ? <span className="font-medium text-orange-600">{v}</span> : <span className="text-slate-300">0</span>) },
    { title: 'Dung lượng', dataIndex: 'size', width: 100, align: 'right', sorter: (a, b) => a.size - b.size, render: v => <span className="text-xs text-slate-500">{fmtSize(v)}</span> },
  ];

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="text-sm text-slate-500">Phân tích theo</span>
        <Segmented<Dimension> value={dim} onChange={setDim} options={(Object.keys(DIMENSION_LABEL) as Dimension[]).map(k => ({ value: k, label: DIMENSION_LABEL[k] }))} />
      </div>

      <Card title={`Quy mô và tỷ lệ đọc theo ${DIMENSION_LABEL[dim].toLowerCase()}`} hint={rows.length > 12 ? 'hiển thị 12 nhóm lớn nhất' : undefined} className="mb-4">
        <Legend items={[{ label: 'Đang hiệu lực', color: COLORS.brand }, { label: 'Trạng thái khác', color: '#CBD5E1' }, { label: 'Tỷ lệ đọc (trục phải)', color: COLORS.amber, line: true }]} />
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chart} margin={{ top: 8, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#eef2f5" />
              <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#64748b' }} interval={0} angle={chart.length > 6 ? -20 : 0} textAnchor={chart.length > 6 ? 'end' : 'middle'} height={chart.length > 6 ? 56 : 30} />
              <YAxis yAxisId="l" allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `${v}%`} />
              <ChartTooltip formatter={(v, n) => (n === 'read' ? [`${v}%`, 'Tỷ lệ đọc'] : [`${v} tài liệu`, n === 'active' ? 'Đang hiệu lực' : 'Trạng thái khác'])} />
              <Bar yAxisId="l" dataKey="active" stackId="a" fill={COLORS.brand} barSize={28} />
              <Bar yAxisId="l" dataKey="other" stackId="a" fill="#CBD5E1" radius={[4, 4, 0, 0]} barSize={28} />
              <Line yAxisId="r" dataKey="read" stroke={COLORS.amber} strokeWidth={2} dot={{ r: 3 }} type="monotone" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="Bảng so sánh chi tiết" hint={dim === 'creator' ? 'theo người tạo' : 'bấm một dòng để mở danh sách đã lọc'}>
        <Table<DimRow>
          size="middle" rowKey="key" columns={columns} dataSource={rows} scroll={{ x: 1150 }} pagination={false}
          onRow={r => (r.filter ? { onClick: () => ctx.goList(r.filter!), className: 'cursor-pointer' } : {})}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row className="bg-slate-50 font-semibold">
                <Table.Summary.Cell index={0}>Tổng cộng</Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">{sum('total')}</Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">{sum('active')}</Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">{sum('issued')}</Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">{sum('pending')}</Table.Summary.Cell>
                <Table.Summary.Cell index={5}>
                  {(() => { const pairs = sum('readPairs'); const read = rows.reduce((s, r) => s + r.readRate * r.readPairs, 0); return pairs ? pct(read / pairs) : '—'; })()}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} />
                <Table.Summary.Cell index={7} />
                <Table.Summary.Cell index={8} align="right">{sum('expiring')}</Table.Summary.Cell>
                <Table.Summary.Cell index={9} align="right">{fmtSize(sum('size'))}</Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>
    </>
  );
}
